import React from 'react';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { reverse } from 'named-urls';

import Grid, { defaultOperators, defaultRenderers } from 'components/Grid';

import { ColumnDefinition } from 'components/Grid/types';

import { useStore } from 'utils/hooks/store';

import routes from 'core/routes';

import EntityActionCell from 'components/Entities/EntityActionCell';

import DueDate from 'components/DueDate';

import { formatFloating } from 'utils/formatters/timezone';

import { ExtendedUserOperators } from 'components/Grid/operators/User/User';

import { ProtectorType } from 'core/constants';

import { RiskAssessmentsTableProps } from './types';

export const basicRiskAssessmentsColumns = [
  {
    field: 'riskAssessments__name',
    headerName: 'Name',
    protectorType: ProtectorType.ShortText,
  },
  {
    field: 'riskAssessments__owner',
    headerName: 'Owner',
    renderCell: defaultRenderers.user,
    filterOperators: ExtendedUserOperators(),
    valueGetter: (value: unknown) => ({ value }),
    protectorType: ProtectorType.User,
  },
  {
    field: 'riskAssessments__stage',
    headerName: 'Stage',
    renderCell: defaultRenderers.single_choice,
    protectorType: ProtectorType.SingleChoice,
  },
  {
    field: 'riskAssessments__assessmentDate',
    headerName: 'Assessment Date',
    renderCell: ({ value }: { value: { value: string } }) => (
      <p>{value.value ? formatFloating(value.value, 'date') : 'N/A'}</p>
    ),
    filterOperators: defaultOperators.date_time(),
    nativeFilter: true,
    customColumn: true,
    protectorType: ProtectorType.DateTime,
  },
  {
    field: 'riskAssessments__reviewDueDate',
    headerName: 'Review Due Date',
    renderCell: ({ value }: { value: { value: string } }) => (value.value ? <DueDate date={value.value} /> : 'N/A'),
    filterOperators: defaultOperators.date(),
    nativeFilter: true,
    customColumn: true,
    protectorType: ProtectorType.DateTime,
  },
] as unknown as ColumnDefinition[];

const RiskAssessmentsTable: React.FunctionComponent<RiskAssessmentsTableProps> = ({
  onError,
  onRefreshCallback,
}): React.ReactElement => {
  const { routing, riskAssessmentDetails } = useStore();

  const onRowClick: GridEventListener<'rowClick'> = (item: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.riskAssessments.details, { entityId: item.id }));
  };

  return (
    <Grid
      basicColumns={
        [
          ...basicRiskAssessmentsColumns,
          {
            field: 'actions',
            type: 'actions',
            headerName: '',
            maxWidth: 50,
            ariaLabel: 'actions_cell',
            renderCell: ({ row, colDef }: { row: { uuid: string }; colDef: ColumnDefinition }) => (
              <EntityActionCell rowData={row} actions={colDef.actions} store={riskAssessmentDetails} />
            ),
            filterOperators: null,
            sortable: false,
          },
        ] as unknown as ColumnDefinition[]
      }
      onError={onError}
      onRowClick={onRowClick}
      disableSelectAllColumns
      onlyBasicExport={false}
      entity="riskAssessments"
      onRefreshCallback={onRefreshCallback}
      dataURL="/api/entities/riskAssessments/"
      fieldsURL="/api/entities/riskAssessments/fields/"
      customNoRowsText="No risk assessments have been created yet"
    />
  );
};

export default RiskAssessmentsTable;
